import React from 'react';
import { X, CalendarIcon, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select.jsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { AppointmentStatus } from '@/types/appointment-types';

type DateRange = {
  from: Date;
  to: Date;
} | null;

interface AppointmentFiltersProps {
  filters: {
    status: string;
    dateRange: DateRange;
    searchQuery: string;
  };
  setFilters: React.Dispatch<React.SetStateAction<{
    status: string;
    dateRange: DateRange;
    searchQuery: string;
  }>>;
}

const AppointmentFilters: React.FC<AppointmentFiltersProps> = ({ filters, setFilters }) => {
  // Status options for filter dropdown
  const statusOptions: { value: AppointmentStatus | ''; label: string; color: string }[] = [
    { value: '', label: 'All statuses', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
    { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-900/50' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-900/50' },
    { value: 'completed', label: 'Completed', color: 'bg-gray-50 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border-gray-200 dark:border-gray-800/50' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-900/50' },
    { value: 'rescheduled', label: 'Rescheduled', color: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-900/50' },
  ];

  // Function to handle status filter change
  const handleStatusChange = (value: string) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  // Function to handle date range filter change
  const handleDateRangeChange = (range: DateRange) => {
    setFilters((prev) => ({ ...prev, dateRange: range }));
  };

  // Function to clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      dateRange: null,
      searchQuery: '',
    });
  };

  // Check if any filters are applied
  const hasActiveFilters = filters.status !== '' || filters.dateRange !== null || filters.searchQuery !== '';

  // Get the color for the status badge
  const getStatusColor = (status: string) => {
    return statusOptions.find(opt => opt.value === status)?.color || statusOptions[0].color;
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-medium flex items-center">
          <Filter className="h-4 w-4 mr-2 text-indigo-500" />
          Active Filters
        </h3>
        {hasActiveFilters && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearFilters} 
            className="h-8 px-3 border-red-100 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <X className="h-4 w-4 mr-1.5" />
            Clear all filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Status filter */}
        <div className="space-y-2.5">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Appointment Status</label>
          <Select
            value={filters.status}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-10">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                  <div className="flex items-center">
                    {option.value && (
                      <div className={`h-2.5 w-2.5 rounded-full mr-2 ${option.value === 'scheduled' ? 'bg-blue-500' : 
                                                                        option.value === 'confirmed' ? 'bg-green-500' : 
                                                                        option.value === 'completed' ? 'bg-gray-500' : 
                                                                        option.value === 'cancelled' ? 'bg-red-500' : 
                                                                        option.value === 'rescheduled' ? 'bg-amber-500' : 'bg-slate-500'}`}></div>
                    )}
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date range filter */}
        <div className="space-y-2.5">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Date Range</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-10"
              >
                <CalendarIcon className="h-4 w-4 mr-2 text-indigo-500" />
                {filters.dateRange?.from ? (
                  filters.dateRange.to ? (
                    <>
                      {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                      {format(filters.dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(filters.dateRange.from, "LLL dd, y")
                  )
                ) : (
                  <span className="text-muted-foreground">Select date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 border-slate-200 dark:border-slate-700" align="start">
              <Calendar
                mode="range"
                defaultMonth={filters.dateRange?.from}
                selected={filters.dateRange as any}
                onSelect={handleDateRangeChange as any}
                numberOfMonths={2}
                className="rounded-md border-0"
              />
              <div className="flex items-center justify-end gap-2 border-t border-slate-200 dark:border-slate-700 p-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDateRangeChange(null)}
                  className="h-8"
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => document.body.click()}
                  className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  Apply Range
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Applied filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 pb-1">
          {filters.status && (
            <Badge 
              variant="outline" 
              className={`gap-1 px-2.5 py-1.5 rounded-md border ${getStatusColor(filters.status)}`}
            >
              {statusOptions.find(opt => opt.value === filters.status)?.label || filters.status}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStatusChange('')}
                className="h-5 w-5 p-0 ml-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/20"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.dateRange && (
            <Badge 
              variant="outline" 
              className="gap-1 px-2.5 py-1.5 rounded-md border bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-900/50"
            >
              {format(filters.dateRange.from, "MMM d, yyyy")} - {filters.dateRange.to ? format(filters.dateRange.to, "MMM d, yyyy") : "..."}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDateRangeChange(null)}
                className="h-5 w-5 p-0 ml-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/20"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {filters.searchQuery && (
            <Badge 
              variant="outline" 
              className="gap-1 px-2.5 py-1.5 rounded-md border bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-900/50"
            >
              Search: "{filters.searchQuery}"
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, searchQuery: '' }))}
                className="h-5 w-5 p-0 ml-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/20"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentFilters; 